import React, { useEffect, useState, useContext, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  Switch
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import Grid from 'modules/platform-components/grid/Grid';
import { apiPaths, getAdminData, postData, deleteData, postInstituteAppData, deleteInstituteAppData } from 'modules/platform-components/api-integration-util';
import { toast } from 'react-toastify';
import { Toaster } from 'modules/platform-components/Toaster';
import { getUserDetails } from 'modules/platform-components/utils/shared-data.service';
import AddUserModal from './AddUserModal';
import { AppContext } from 'context/AppContext';
import DynamicInput from 'modules/platform-components/dynamic-input/dynamic-input';
import { checkPermission } from 'modules/platform-components/utils/configuration';

const Users = () => {
  // State declarations
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [roles, setRoles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [activeTab, setActiveTab] = useState(() => {
    if (checkPermission('Users', 'All Users')) return 'users';
    if (checkPermission('Users', 'Question Approver')) return 'questionApprovers';
    return 'users';
  });
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    userRole: "",
    roleIds: [],
    branchPermissions: [],
    active: true,
  });
  const [selectedItems, setSelectedItems] = useState([]);
  const [formState, setFormState] = useState(false);
  const [questionApproversData, setQuestionApproversData] = useState([]);

  const { subjects, userSubjectMap, getUserSubjectMap, getSubjects, users: allUsers } = useContext(AppContext);
  const hasLoadedQuestionApprovers = useRef(false);

  // Initialize configData
  const [configData, setConfigData] = useState({
    subjectsList: subjects || [],
    usersList: allUsers || [],
    confirm: [{ key: "Yes", value: true }, { key: "No", value: false }]
  });

  // Fetch functions
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAdminData(apiPaths.acadUserList, {
        params: { userEmail: getUserDetails().email },
        headers: {
          tenantId: getUserDetails().tenantId,
          userEmail: getUserDetails().email
        }
      });

      if (response?.result) {
        const formattedUsers = response.result.map(user => ({
          ...user,
          id: user.userId,
          userRole: user.userRoles?.[0]?.roleName || 'No Role',
        }));
        setUsers(formattedUsers);
        setFilteredUsers(formattedUsers);
        setConfigData(prev => ({
          ...prev,
          usersList: formattedUsers
        }));
      }
    } catch (err) {
      setError('Failed to fetch users');
      console.error('Error fetching users:', err);
      Toaster(toast.TYPE.ERROR, err.response?.data?.msg || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBranches = useCallback(async () => {
    try {
      const response = await getAdminData(apiPaths.branches, {
        headers: {
          tenantId: getUserDetails().tenantId,
          userEmail: getUserDetails().email
        }
      });
      if (response?.result) {
        setBranches(response.result);
      }
    } catch (err) {
      console.error('Failed to fetch branches', err);
    }
  }, []);

  // Initial data load
  useEffect(() => {
    fetchUsers();
    fetchBranches();
    fetchRoles();
  }, [fetchUsers, fetchBranches]);

  const fetchRoles = async () => {
    try {
      const response = await getAdminData(apiPaths.acadRoles, {
        headers: {
          tenantId: getUserDetails().tenantId,
          userEmail: getUserDetails().email
        }
      });
      if (response?.result) {
        setRoles(response.result);
      }
    } catch (err) {
      console.error('Failed to fetch roles', err);
    }
  };

  // Question Approver data load
  const loadQuestionApproverData = useCallback(async () => {
    if (!hasLoadedQuestionApprovers.current) {
      try {
        setLoading(true);
        await getUserSubjectMap();
        await getSubjects();

        if (userSubjectMap && userSubjectMap.length > 0) {
          const formattedData = userSubjectMap.map(item => {
            const user = allUsers.find(u => u.userId === item.userId);
            const subject = subjects.find(s => s.subjectId === item.subjectId);
            return {
              ...item,
              userName: user ? `${user.firstName} ${user.lastName}` : 'Unknown User',
              subjectName: subject ? subject.subject : 'Unknown Subject',
              createdDate: item.createdDate || new Date().toISOString()
            };
          });
          setQuestionApproversData(formattedData);
        }
      } catch (error) {
        console.error('Error loading question approvers:', error);
        Toaster(toast.TYPE.ERROR, 'Failed to load question approvers');
      } finally {
        setLoading(false);
        hasLoadedQuestionApprovers.current = true;
      }
    }
  }, [getUserSubjectMap, getSubjects, userSubjectMap, subjects, allUsers]);

  useEffect(() => {
    if (activeTab === 'questionApprovers') {
      loadQuestionApproverData();
    }
  }, [activeTab, loadQuestionApproverData]);

  // Update config data when context changes
  useEffect(() => {
    setConfigData(prev => ({
      ...prev,
      subjectsList: subjects || [],
      usersList: allUsers || []
    }));
  }, [subjects, allUsers]);

  // Filter users
  useEffect(() => {
    let result = [...users];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(user =>
        user.firstName?.toLowerCase().includes(term) ||
        user.lastName?.toLowerCase().includes(term) ||
        user.email?.toLowerCase().includes(term) ||
        user.userId?.toString().includes(term)
      );
    }

    if (roleFilter) {
      result = result.filter(user =>
        user.userRoles?.some(role =>
          role.roleName?.toLowerCase().includes(roleFilter.toLowerCase())
        )
      );
    }

    setFilteredUsers(result);
  }, [users, searchTerm, roleFilter]);

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBranchChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      branchPermissions: typeof value === 'string' ? value.split(',') : value
    }));
  };

  const handleRoleChange = (roleId) => {
    setFormData(prev => ({
      ...prev,
      userRole: roleId,
      roleIds: [roleId]
    }));
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      userRole: '',
      roleIds: [],
      branchPermissions: [],
      active: true
    });
  };

  // User CRUD operations
  const handleAddUser = async () => {
    try {
      const userData = {
        userId: 0,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        active: formData.active,
        roleId: formData.roleIds[0],
        roleIds: formData.roleIds,
        userRoles: formData.roleIds.map(roleId => ({
          roleId: roleId,
          roleName: roles.find(r => r.roleId === roleId)?.roleName || "",
          rolePermission: "",
          userId: 0,
          userRoleId: 0
        })),
        createdBy: getUserDetails().email,
        createdDate: new Date().toISOString(),
        updatedBy: getUserDetails().email,
        updatedDate: new Date().toISOString(),
        tenantId: getUserDetails().tenantId,
        token: ""
      };

      const response = await postInstituteAppData(apiPaths.acadSaveUser, {
        data: userData,
        headers: {
          tenantId: getUserDetails().tenantId,
          'Content-Type': 'application/json'
        },
        params: {
          userEmail: getUserDetails().email
        }
      });

      if (response?.result) {
        const newUser = {
          ...response.result,
          id: response.result.userId,
          userRole: response.result.userRoles?.[0]?.roleName || 'No Role'
        };
        setUsers(prev => [...prev, newUser]);
        setOpenAddDialog(false);
        resetForm();
        Toaster(toast.TYPE.SUCCESS, 'User added successfully');
      }
    } catch (err) {
      console.error('Failed to add user:', err);
      Toaster(toast.TYPE.ERROR, err.response?.data?.msg || 'Failed to add user');
    }
  };

  const handleEditUser = (user) => {
    setCurrentUser(user);
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      userRole: user.userRoles?.[0]?.roleId || '',
      roleIds: user.roleIds || (user.userRoles ? user.userRoles.map(r => r.roleId) : []),
      branchPermissions: user.branchPermissions || [],
      active: user.active
    });
    setOpenEditDialog(true);
  };

  const handleUpdateUser = async () => {
    try {
      const userData = {
        userId: currentUser.userId,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        active: formData.active,
        roleId: formData.roleIds[0],
        roleIds: formData.roleIds,
        userRoles: formData.roleIds.map(roleId => ({
          roleId: roleId,
          roleName: roles.find(r => r.roleId === roleId)?.roleName || "",
          rolePermission: "",
          userId: currentUser.userId,
          userRoleId: currentUser.userRoles?.find(r => r.roleId === roleId)?.userRoleId || 0
        })),
        updatedBy: getUserDetails().email,
        updatedDate: new Date().toISOString(),
        tenantId: getUserDetails().tenantId
      };

      const response = await postInstituteAppData(apiPaths.acadSaveUser, {
        data: userData,
        headers: {
          tenantId: getUserDetails().tenantId,
          'Content-Type': 'application/json'
        },
        params: {
          userEmail: getUserDetails().email
        }
      });

      if (response?.result) {
        const updatedUser = {
          ...response.result,
          id: response.result.userId,
          userRole: response.result.userRoles?.[0]?.roleName || 'No Role'
        };
        setUsers(prev => prev.map(user => user.userId === currentUser.userId ? updatedUser : user));
        setOpenEditDialog(false);
        resetForm();
        Toaster(toast.TYPE.SUCCESS, 'User updated successfully');
      }
    } catch (err) {
      console.error('Failed to update user:', err);
      Toaster(toast.TYPE.ERROR, err.response?.data?.msg || 'Failed to update user');
    }
  };

  const handleDeleteUser = async () => {
    try {
      const response = await deleteInstituteAppData(apiPaths.acadDeleteUser, {
        body: [userToDelete],
        params: {
          userEmail: getUserDetails().email,
        },
        headers: {
          tenantId: getUserDetails().tenantId,
        }
      });

      if (response?.result) {
        setUsers(prev => prev.filter(user => user.userId !== userToDelete));
        setOpenDeleteDialog(false);
        setUserToDelete(null);
        Toaster(toast.TYPE.SUCCESS, 'User deleted successfully');
      }
    } catch (err) {
      console.error('Failed to delete user:', err);
      Toaster(toast.TYPE.ERROR, err.response?.data?.msg || 'Failed to delete user');
    }
  };

  // Question Approver functions
  const handleQuestionApproverOutput = (data, tabInfo) => {
    setFormState(false);

    if (tabInfo?.httpMethod === 'DELETE') {
      deleteData(`${tabInfo?.servicePath}?userId=${selectedItems[0]?.userId}&subjectId=${selectedItems[0]?.subjectId}`, {
        data: selectedItems,
      })
        .then((res) => {
          Toaster(toast.TYPE.SUCCESS, `${tabInfo?.toasterMsg}`);
          loadQuestionApproverData();
          setFormState(true);
        })
        .catch((e) => {
          Toaster(toast.TYPE.ERROR, 'Error occurred, Please try again');
        });
    } else {
      data = {
        "subjectId": data.subjectId,
        "userIds": [data.userId]
      };

      data.createdBy = getUserDetails().email;
      postData(tabInfo?.servicePath, {
        data: data,
      })
        .then((res) => {
          Toaster(toast.TYPE.SUCCESS, `${tabInfo?.toasterMsg}`);
          loadQuestionApproverData();
          setFormState(true);
        })
        .catch((e) => {
          Toaster(toast.TYPE.ERROR, 'Error occurred, Please try again');
        });
    }
  };

  // Helper functions
  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Grid configurations
  const columnDefs = [
    { headerName: "User ID", field: "userId", filter: true },
    { headerName: "First Name", field: "firstName", filter: true },
    { headerName: "Last Name", field: "lastName", filter: true },
    { headerName: "Email", field: "email", filter: true },
    {
      headerName: "Roles",
      field: "userRoles",
      valueFormatter: (params) => params.value?.map(role => role.roleName).join(', ') || 'None'
    },
    {
      headerName: "Branch Permissions",
      field: "branchPermissions",
      valueFormatter: (params) => Array.isArray(params.value)
        ? params.value.join(', ')
        : params.value,
      filter: true
    },
    {
      headerName: "Active",
      field: "active",
      cellRendererFramework: (params) => (
        <Switch
          checked={params.data.active}
          onChange={() => handleEditUser({ ...params.data, active: !params.data.active })}
          color="primary"
          size="small"
        />
      )
    },
    { headerName: "Created By", field: "createdBy", filter: true },
    {
      headerName: "Created Date",
      field: "createdDate",
      filter: true,
      valueFormatter: (params) => formatDate(params.value)
    },
    {
      headerName: "Updated Date",
      field: "updatedDate",
      filter: true,
      valueFormatter: (params) => formatDate(params.value)
    },
    {
      headerName: "Actions",
      field: "actions",
      cellRendererFramework: (params) => (
        <div>
          <IconButton
            onClick={() => handleEditUser(params.data)}
            color="primary"
            size="small"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            onClick={() => {
              setUserToDelete(params.data.userId);
              setOpenDeleteDialog(true);
            }}
            color="error"
            size="small"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </div>
      )
    },
  ];

  const gridOptions = {
    columnDefs,
    rowData: filteredUsers,
    pagination: true,
    paginationPageSize: 10,
  };

  const questionApproverColumns = [
    { headerName: "User ID", field: "userId", width: 100 },
    { headerName: "User Name", field: "userName", width: 200 },
    {
      headerName: "Subject",
      field: "subjectName",
      cellRenderer: (params) => {
        const subject = subjects.find(sub => sub.subjectId === params.data.subjectId);
        return subject ? subject.subject : 'Unknown Subject';
      },
      width: 200
    },
    { headerName: "Created By", field: "createdBy", width: 150 },
    {
      headerName: "Created Date",
      field: "createdDate",
      valueFormatter: (params) => formatDate(params.value),
      width: 150
    }
  ];

  const questionApproverGridOptions = {
    columnDefs: questionApproverColumns,
    rowData: questionApproversData,
    showGrid: true,
    rowSelection: 'multiple',
    pagination: true,
    paginationPageSize: 20,
    onSelectionChanged: (params) => {
      setSelectedItems(
        params.api.getSelectedRows().map((e) => ({
          userId: e.userId,
          subjectId: e.subjectId
        }))
      );
    },
    onGridReady: (params) => {
      params.api.sizeColumnsToFit();
    }
  };

  // Render Question Approver tab
  const renderQuestionApprovers = () => (
    <Box>
      <DynamicInput
        OutputEvent={handleQuestionApproverOutput}
        tabName="USER_SUBJECT_MAP"
        config={configData}
        formReset={formState}
      />

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : questionApproversData.length > 0 ? (
        <div style={{ height: '500px', width: '100%', marginTop: '20px' }}>
          <Grid
            showGrid
            gridOptions={questionApproverGridOptions}
            parent="questionApprovers"
          />
        </div>
      ) : (
        <Typography variant="body1" sx={{ mt: 3, textAlign: 'center' }}>
          No question approvers found. Please add new mappings.
        </Typography>
      )}
    </Box>
  );

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        User Management
      </Typography>

      <Box sx={{ width: '100%', typography: 'body1', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          {checkPermission('Users', 'All Users') && (
            <Tab label="All Users" value="users" />
          )}
          {checkPermission('Users', 'Question Approver') && (
            <Tab label="Question Approvers" value="questionApprovers" />
          )}
        </Tabs>

        {activeTab === 'users' && (
          <Box display="flex" justifyContent="flex-end" alignItems="center" mb={3}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenAddDialog(true)}
              sx={{
                color: '#3366FF',
                backgroundColor: 'transparent',
                '&:hover': { backgroundColor: '#3366FF', color: 'white' },
                fontWeight: '600',
                textTransform: 'none',
                borderRadius: '6px'
              }}
            >
              ADD USER
            </Button>
          </Box>
        )}
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <>
          {activeTab === 'users' ? (
            <Grid
              gridOptions={gridOptions}
              parent="users"
            />
          ) : (
            renderQuestionApprovers()
          )}
        </>
      )}

      <AddUserModal
        open={openAddDialog}
        onClose={() => {
          setOpenAddDialog(false);
          resetForm();
        }}
        formData={formData}
        handleInputChange={handleInputChange}
        handleBranchChange={handleBranchChange}
        handleRoleChange={handleRoleChange}
        handleSubmit={handleAddUser}
        roles={roles}
        branches={branches}
      />

      <AddUserModal
        open={openEditDialog}
        onClose={() => {
          setOpenEditDialog(false);
          resetForm();
        }}
        formData={formData}
        handleInputChange={handleInputChange}
        handleBranchChange={handleBranchChange}
        handleRoleChange={handleRoleChange}
        handleSubmit={handleUpdateUser}
        roles={roles}
        branches={branches}
        isEditMode={true}
        currentUser={currentUser}
      />

      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Confirm Delete
          <IconButton onClick={() => setOpenDeleteDialog(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this user? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDeleteUser}
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;